// src/connection/websocket.ts

import type {
  ConnectionConfig,
  ConnectionEvents,
  ConnectionState,
  BrowserToRelayMessage,
  RelayToBrowserMessage,
  ErrorCode,
  SortBy,
  SortOrder,
} from '../types';
import { encodeMessage, decodeMessage, encodeData } from './protocol';
import { ConnectionStateMachine } from './state';
import { generateEphemeralKeypair, deriveSessionKey, decodePublicKey, encodePublicKey } from '../e2ee/keys';
import { E2EESession } from '../e2ee/session';
import { verifyAgentKey } from '../e2ee/tofu';
import type { E2EEState, KeyExchangeResult } from '../e2ee/types';

const DEFAULT_CONFIG = {
  connectTimeout: 10000,
  autoReconnect: true,
  maxReconnectAttempts: 10,
  reconnectDelay: 1000,
  maxReconnectDelay: 30000,
};

export class ConnectionManager {
  private config: Required<ConnectionConfig>;
  private events: ConnectionEvents;
  private stateMachine: ConnectionStateMachine;

  private ws: WebSocket | null = null;
  private sessionId: string | null = null;

  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null;

  private messageQueue: BrowserToRelayMessage[] = [];

  // E2EE fields
  private e2eeSessions: Map<string, E2EESession> = new Map();
  private e2eeState: E2EEState = 'not_initialized';
  private agentPublicKey: string | null = null;
  private subdomain: string | null = null;

  constructor(config: ConnectionConfig, events: ConnectionEvents) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.events = events;
    this.stateMachine = new ConnectionStateMachine();

    this.stateMachine.onStateChange((state) => {
      this.events.onStateChange(state);
    });
  }

  getState(): ConnectionState {
    return this.stateMachine.getState();
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  connect(): void {
    const state = this.stateMachine.getState();
    if (state !== 'disconnected' && state !== 'error') {
      return;
    }

    this.stateMachine.transition('connecting');
    this.createWebSocket();
  }

  disconnect(): void {
    this.clearTimers();
    this.reconnectAttempts = 0;
    this.messageQueue = [];

    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }

    this.sessionId = null;
    this.stateMachine.transition('disconnected');
  }

  sendInput(data: string, sessionId?: string): void {
    const msg: BrowserToRelayMessage = sessionId
      ? { type: 'input', data: encodeData(data), session_id: sessionId }
      : { type: 'input', data: encodeData(data) };
    this.send(msg);
  }

  sendResize(rows: number, cols: number, sessionId?: string): void {
    const msg: BrowserToRelayMessage = sessionId
      ? { type: 'resize', rows, cols, session_id: sessionId }
      : { type: 'resize', rows, cols };
    this.send(msg);
  }

  sendPing(): void {
    this.send({ type: 'ping' });
  }

  /**
   * Request creation of a new session (tab)
   */
  createSession(tabId: string, restoreSessionId?: string): void {
    const msg: BrowserToRelayMessage = restoreSessionId
      ? { type: 'create_session', tab_id: tabId, restore_session_id: restoreSessionId }
      : { type: 'create_session', tab_id: tabId };
    this.send(msg);
  }

  /**
   * Request closing a session
   */
  closeSession(sessionId: string): void {
    this.send({ type: 'close_session', session_id: sessionId });
  }

  /**
   * Request scrollback data for a session
   */
  getScrollback(sessionId: string, maxLines?: number): void {
    const msg: BrowserToRelayMessage = maxLines !== undefined
      ? { type: 'get_scrollback', session_id: sessionId, lines: maxLines }
      : { type: 'get_scrollback', session_id: sessionId };
    this.send(msg);
  }

  /**
   * Enable or disable scrollback persistence for a session
   */
  setScrollbackEnabled(sessionId: string, enabled: boolean): void {
    this.send({ type: 'set_scrollback_enabled', session_id: sessionId, enabled });
  }

  /**
   * Clear scrollback for a session
   */
  clearScrollback(sessionId: string): void {
    this.send({ type: 'clear_scrollback', session_id: sessionId });
  }

  /**
   * Request directory listing from agent
   */
  listDirectory(
    path: string,
    options: { showHidden?: boolean; sortBy?: SortBy; sortOrder?: SortOrder } = {},
    sessionId?: string
  ): void {
    const targetSessionId = sessionId ?? this.sessionId;
    if (!targetSessionId) {
      throw new Error('No active session');
    }

    if (this.isE2EEActive(targetSessionId)) {
      // Encrypt the ListDir payload
      const payload = JSON.stringify({
        type: 'list_dir',
        path,
        show_hidden: options.showHidden ?? false,
        sort_by: options.sortBy ?? 'name',
        sort_order: options.sortOrder ?? 'ascending',
      });

      const plaintext = new TextEncoder().encode(payload);
      const e2eeSession = this.getE2EESession(targetSessionId);
      if (!e2eeSession) {
        throw new Error('E2EE session not established');
      }
      const { nonce, ciphertext } = e2eeSession.encrypt(plaintext);

      this.send({
        type: 'encrypted_file_request',
        nonce: btoa(String.fromCharCode(...nonce)),
        data: btoa(String.fromCharCode(...ciphertext)),
        session_id: targetSessionId,
      });
    } else {
      this.send({
        type: 'list_dir',
        session_id: targetSessionId,
        path,
        show_hidden: options.showHidden ?? false,
        sort_by: options.sortBy ?? 'name',
        sort_order: options.sortOrder ?? 'ascending',
      });
    }
  }

  /**
   * Initialize E2EE with agent's public key (from session token response)
   */
  initializeE2EE(agentPublicKey: string, subdomain: string): KeyExchangeResult {
    this.agentPublicKey = agentPublicKey;
    this.subdomain = subdomain;

    // Verify key using TOFU
    const result = verifyAgentKey(subdomain, agentPublicKey);

    if (result.success) {
      this.e2eeState = 'key_exchange_pending';
    } else {
      this.e2eeState = 'failed';
    }

    return result;
  }

  /**
   * Perform key exchange after connection is established
   */
  performKeyExchange(sessionId: string): void {
    if (!this.agentPublicKey) {
      throw new Error('E2EE not initialized');
    }
    if (!sessionId) {
      throw new Error('Missing session_id for key exchange');
    }
    if (this.e2eeSessions.has(sessionId)) {
      return;
    }

    // Generate ephemeral keypair
    const { privateKey, publicKey } = generateEphemeralKeypair();

    // Derive session key
    const agentPubBytes = decodePublicKey(this.agentPublicKey);
    const sessionKey = deriveSessionKey(privateKey, agentPubBytes);

    // Create E2EE session
    const e2eeSession = new E2EESession(sessionKey);
    this.e2eeSessions.set(sessionId, e2eeSession);
    this.e2eeState = 'active';

    // Send our public key to agent via relay
    this.send({
      type: 'key_exchange',
      browser_public_key: encodePublicKey(publicKey),
      session_id: sessionId,
    });
  }

  /**
   * Send encrypted input
   */
  sendEncryptedInput(data: string, sessionId?: string): void {
    const targetSessionId = sessionId ?? this.getSingleE2EESessionId();
    if (!targetSessionId) {
      throw new Error('Missing session_id for encrypted input');
    }
    const e2eeSession = this.getE2EESession(targetSessionId);
    if (!e2eeSession) {
      throw new Error('E2EE session not established');
    }

    const plaintext = new TextEncoder().encode(data);
    const { nonce, ciphertext } = e2eeSession.encrypt(plaintext);

    const msg: BrowserToRelayMessage = {
      type: 'encrypted_input',
      nonce: btoa(String.fromCharCode(...nonce)),
      data: btoa(String.fromCharCode(...ciphertext)),
      session_id: targetSessionId,
    };
    this.send(msg);
  }

  /**
   * Decrypt received output
   */
  decryptOutput(nonce: string, data: string, sessionId?: string): string {
    const targetSessionId = sessionId ?? this.getSingleE2EESessionId();
    if (!targetSessionId) {
      throw new Error('Missing session_id for decryption');
    }
    const e2eeSession = this.getE2EESession(targetSessionId);
    if (!e2eeSession) {
      throw new Error('E2EE session not established');
    }

    const nonceBytes = Uint8Array.from(atob(nonce), c => c.charCodeAt(0));
    const ciphertext = Uint8Array.from(atob(data), c => c.charCodeAt(0));

    const plaintext = e2eeSession.decrypt(nonceBytes, ciphertext);
    return new TextDecoder().decode(plaintext);
  }

  /**
   * Check if E2EE is active
   */
  isE2EEActive(sessionId?: string): boolean {
    if (sessionId) {
      return this.e2eeSessions.has(sessionId);
    }
    return this.e2eeSessions.size > 0 && this.e2eeState === 'active';
  }

  /**
   * Get E2EE state
   */
  getE2EEState(): E2EEState {
    return this.e2eeState;
  }

  private getE2EESession(sessionId: string): E2EESession | null {
    return this.e2eeSessions.get(sessionId) ?? null;
  }

  private getSingleE2EESessionId(): string | null {
    if (this.e2eeSessions.size === 1) {
      return Array.from(this.e2eeSessions.keys())[0] ?? null;
    }
    return null;
  }

  /**
   * Get agent public key fingerprint
   */
  getAgentKeyFingerprint(): string | null {
    if (!this.agentPublicKey) return null;
    return this.agentPublicKey.substring(0, 8);
  }

  /**
   * Get the subdomain associated with this connection
   */
  getSubdomain(): string | null {
    return this.subdomain;
  }

  private send(msg: BrowserToRelayMessage): void {
    if (this.stateMachine.getState() !== 'ready') {
      this.messageQueue.push(msg);
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(encodeMessage(msg));
    }
  }

  private createWebSocket(): void {
    const url = new URL(this.config.relayUrl);
    url.searchParams.set('token', this.config.token);

    this.ws = new WebSocket(url.toString());

    this.connectTimer = setTimeout(() => {
      if (this.stateMachine.getState() === 'connecting') {
        this.ws?.close();
        this.handleError(new Error('Connection timeout'));
      }
    }, this.config.connectTimeout);

    this.ws.onopen = () => {
      this.clearConnectTimer();
      this.startKeepAlive();
      this.stateMachine.transition('connected');
    };

    this.ws.onmessage = (event: MessageEvent<string | ArrayBuffer>) => {
      this.handleMessage(event.data);
    };

    this.ws.onerror = () => {
      this.handleError(new Error('WebSocket error'));
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.handleClose(event);
    };
  }

  private handleMessage(data: string | ArrayBuffer): void {
    try {
      const text = typeof data === 'string' 
        ? data 
        : new TextDecoder().decode(data);

      const msg: RelayToBrowserMessage = decodeMessage(text);

      switch (msg.type) {
        case 'connected':
          // Initial connection established - wait for session_pending/session_ready
          this.reconnectAttempts = 0;
          this.stateMachine.transition('connected');

          // If session_id is provided in connected message, session is ready
          if (msg.session_id) {
            this.sessionId = msg.session_id;
            this.stateMachine.transition('ready');
            this.events.onSessionReady(msg.session_id);
            this.flushMessageQueue();
          }
          break;

        case 'session_pending':
          // Session is being established with agent
          this.sessionId = msg.session_id;
          // Ensure we're in connected state
          if (this.stateMachine.getState() === 'connecting') {
            this.stateMachine.transition('connected');
          }
          break;

        case 'session_ready':
          // Session confirmation from agent (via relay). This fires after CreateSession
          // succeeds. Do NOT call onSessionReady again — that's only for initial connection.
          // Just update the session ID and ensure we're in ready state.
          this.sessionId = msg.session_id;
          this.reconnectAttempts = 0;

          if (this.e2eeState === 'key_exchange_pending' && this.agentPublicKey) {
            if (!this.e2eeSessions.has(msg.session_id)) {
              this.performKeyExchange(msg.session_id);
            }
          }
          
          if (this.stateMachine.getState() !== 'ready') {
            this.stateMachine.transition('ready');
            this.events.onSessionReady(msg.session_id);
            this.flushMessageQueue();
          }
          // If already ready, this is just a session confirmation — ignore.
          break;

        case 'output':
          // Legacy unencrypted output
          this.events.onMessage(msg);
          break;

        case 'encrypted_output':
          // Decrypt and forward as regular output, preserving session_id for routing
          try {
            const plaintext = this.decryptOutput(msg.nonce, msg.data, msg.session_id);
            const outputMsg: RelayToBrowserMessage = { type: 'output', data: plaintext };
            if (msg.session_id) {
              (outputMsg as { session_id?: string }).session_id = msg.session_id;
            }
            this.events.onMessage(outputMsg);
          } catch {
            this.events.onError(new Error('Decryption failed'));
          }
          break;

        case 'connection_failed':
          this.events.onError(new Error(`${msg.code}: ${msg.message}`));
          this.disconnect();
          break;

        case 'session_ended': {
          const endedSid = (msg as { session_id?: string }).session_id ?? this.sessionId;
          console.warn(`[WHSS] session_ended received: reason=${msg.reason} exit_code=${msg.exit_code} session_id=${endedSid}`);
          // If this is the legacy session (the one the relay auto-created), clear it
          if (endedSid === this.sessionId) {
            this.sessionId = null;
          }
          // Forward to onMessage so the tab store can handle per-session cleanup.
          // Do NOT disconnect the entire WebSocket — other sessions are still alive.
          this.events.onMessage(msg);
          break;
        }

        case 'error':
          this.events.onError(new Error(`${msg.code}: ${msg.message}`));
          if (this.isAuthError(msg.code)) {
            this.disconnect();
          }
          break;

        case 'server_shutdown':
          this.events.onError(new Error(`Server shutting down: ${msg.reason}`));
          break;

        case 'pong':
          // Pong response - could be used for latency measurement
          break;

        case 'dir_contents':
        case 'cwd_changed':
          // File Explorer messages - pass through to onMessage handler
          this.events.onMessage(msg);
          break;

        case 'session_created':
          if (this.e2eeState === 'key_exchange_pending' && this.agentPublicKey) {
            if (!this.e2eeSessions.has(msg.session_id)) {
              this.performKeyExchange(msg.session_id);
            }
          }
          this.events.onMessage(msg);
          break;

        case 'session_closed':
        case 'scrollback_data':
        case 'session_state_changed':
          // Multi-session messages - pass through to onMessage handler
          this.events.onMessage(msg);
          break;

        case 'encrypted_file_data':
          // Decrypt and forward as dir_contents or cwd_changed
          try {
            const decrypted = this.decryptOutput(msg.nonce, msg.data);
            const parsed = JSON.parse(decrypted) as RelayToBrowserMessage;
            this.events.onMessage(parsed);
          } catch {
            this.events.onError(new Error('Failed to decrypt file data'));
          }
          break;
      }
    } catch (e) {
      console.error('Failed to parse message:', e);
    }
  }

  private handleClose(event: CloseEvent): void {
    console.warn(`[WHSS] WebSocket closed: code=${event.code} reason="${event.reason}" wasClean=${event.wasClean}`);
    this.clearTimers();
    this.ws = null;

    if (event.code === 1000 || event.code === 4001) {
      this.stateMachine.transition('disconnected');
      return;
    }

    if (this.config.autoReconnect && 
        this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      this.stateMachine.transition('disconnected');
    }
  }

  private handleError(error: Error): void {
    console.error(`[WHSS] WebSocket error: ${error.message}`);
    this.clearTimers();
    this.events.onError(error);

    if (this.config.autoReconnect && 
        this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      this.stateMachine.transition('error');
    }
  }

  private scheduleReconnect(): void {
    this.stateMachine.transition('reconnecting');
    this.reconnectAttempts++;

    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.config.maxReconnectDelay
    );
    const jitter = delay * 0.1 * Math.random();

    this.reconnectTimer = setTimeout(() => {
      this.stateMachine.transition('connecting');
      this.createWebSocket();
    }, delay + jitter);
  }

  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const msg = this.messageQueue.shift();
      if (msg) {
        this.send(msg);
      }
    }
  }

  private isAuthError(code: ErrorCode): boolean {
    return ['auth_required', 'auth_invalid', 'auth_expired', 'token_revoked'].includes(code);
  }

  private clearTimers(): void {
    this.clearConnectTimer();
    this.clearReconnectTimer();
    this.stopKeepAlive();
  }

  private clearConnectTimer(): void {
    if (this.connectTimer !== null) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startKeepAlive(): void {
    this.stopKeepAlive();
    // Send ping every 30s to prevent Cloudflare/proxy idle timeout (typically 100s)
    this.keepAliveTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendPing();
      }
    }, 30000);
  }

  private stopKeepAlive(): void {
    if (this.keepAliveTimer !== null) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  dispose(): void {
    this.disconnect();

    // Clean up E2EE resources
    for (const session of this.e2eeSessions.values()) {
      session.dispose();
    }
    this.e2eeSessions.clear();
    this.agentPublicKey = null;
    this.subdomain = null;
    this.e2eeState = 'not_initialized';
  }
}
